import dayjs from "dayjs";
import { apiOrigin } from "../constants/api";

export const host = process.env.REACT_APP_HOST;
const protocol = host.includes("localhost") ? "http://" : "https://";
export const origin = protocol + host;
const withUserProfileId = (url, userProfileId) =>
  userProfileId
    ? `${url}${encodeURIComponent(userProfileId)}/`
    : url;

export const apiUrls = {
  general: () => "/api/general-v2/",
  introspect: (modelName) => `/api/introspect/${modelName}/`,
  push: {
    subscribe: () => "/api/push/subscribe/",
  },
  tags: {
    tags: () => "/api/tags/",
  },
  student: (studentId) => ({
    checkIn: () => `/api/student/v2/${studentId}/check-in/`,
    timeSubmissions: () => `/api/student/v2/${studentId}/time-submission/`,
    currentStudentWeek: () =>
      `/api/student/v2/${studentId}/student-weeks/current/`,
    clockEvents: (clockEventId) =>
      clockEventId
        ? `/api/student/v2/${studentId}/clock/events/${clockEventId}/`
        : `/api/student/v2/${studentId}/clock/events/`,
    clockIn: () => `/api/student/v2/${studentId}/clock/in/`,
    clockOut: () => `/api/student/v2/${studentId}/clock/out/`,
    clockStatus: () => `/api/student/v2/${studentId}/clock/status/`,
    stats: (date) =>
      date
        ? `/api/student/v2/${studentId}/stats/${date}/`
        : `/api/student/v2/${studentId}/stats/`,
    schedule: () => `/api/student/v2/${studentId}/schedule/`,
    scheduleItemCompletions: () =>
      `/api/student/v2/${studentId}/schedule-items/complete/`,
    weekReports: () => `/api/student/v2/${studentId}/week-reports/`,
    goals: () => `/api/student/v2/${studentId}/goals/`,
    awards: () => `/api/student/v2/${studentId}/awards/`,
    rankings: () => `/api/student/v2/${studentId}/rankings/`,
    weekStats: (studentWeekId) =>
      `/api/student/v2/${studentId}/week-stats/${studentWeekId}/`,
    goodNews: (dataType) =>
      `/api/student/v2/${studentId}/good-news/${dataType}/`,
    offDay: () => `/api/student/v2/${studentId}/off-day/`,
    preSubjectReports: () =>
      `/api/student/v2/${studentId}/pre-subject-reports/`,
    lessons: (rowId) => `/api/student/v2/${studentId}/lessons/${rowId}/`,
    tests: (testId) => `/api/student/v2/${studentId}/tests/${testId}/`,
    challenges: (challengeId) =>
      `/api/student/v2/${studentId}/challenges/${challengeId}/`,
    categories: (categoryId) =>
      `/api/student/v2/${studentId}/categories/${categoryId}/`,
  }),
  parent: (parentId) => ({
    students: () => `/api/parent/v2/${parentId}/students/`,
    studentRanking: (studentId) =>
      `/api/parent/v2/${parentId}/students/${studentId}/ranking/`,
    studentAwards: (studentId) =>
      `/api/parent/v2/${parentId}/students/${studentId}/awards/`,
    studentWeeklyData: (studentId) =>
      `/api/parent/v2/${parentId}/students/${studentId}/weekly-data/`,
    goodNews: (dataType) => `/api/parent/v2/${parentId}/good-news/${dataType}/`,
  }),
  coaching: {
    transferStudents: () => "/api/coaching/transfer-students/",
    profiles: (coachProfileId) =>
      coachProfileId
        ? `/api/coaching/profiles/${coachProfileId}/`
        : "/api/coaching/profiles/",
    roleAssignments: (roleAssignmentId) =>
      roleAssignmentId
        ? `/api/coaching/role-assignments/${roleAssignmentId}/`
        : "/api/coaching/role-assignments/",
    reportingEdges: (reportingEdgeId) =>
      reportingEdgeId
        ? `/api/coaching/reporting-edges/${reportingEdgeId}/`
        : "/api/coaching/reporting-edges/",
    hierarchy: () => "/api/coaching/hierarchy/",
    studentToCoachLink: (studentToCoachLinkId) =>
      studentToCoachLinkId
        ? `/api/coaching/student-to-coach-link/${studentToCoachLinkId}/`
        : "/api/coaching/student-to-coach-link/",
    masterRowIntrospect: (rowType, rowId) =>
      `/api/coaching/master-row/${rowType}/${rowId}/`,
  },
  admin: {
    employees: () => "/api/admin/employees/",
    impersonationOptions: () => "/api/admin/impersonation-options/",
    toDoTemplates: () => "/api/admin/to-do-templates/",
    toDoTemplate: (toDoTemplateId) =>
      `/api/admin/to-do-template/${toDoTemplateId}/`,
    toDoTemplateImagesSync: (toDoTemplateId) =>
      `/api/admin/to-do-template/${toDoTemplateId}/images/`,
    studentProcessing: () => "/api/admin/student-processing/",
    tickets: () => "/api/admin/tickets-v2/",
    orders: () => "/api/admin/orders/",
    programWeekToProgram: () => "/api/admin/program-week-to-program/",
    programDayToProgramWeek: () => "/api/admin/program-day-to-program-week/",
    studentToProgram: () => "/api/admin/student-to-program/",
    scheduleItems: () => "/api/admin/schedule-items/",
    programWeeks: () => "/api/admin/program-weeks/",
    programDays: () => "/api/admin/program-days/",
    programs: () => "/api/admin/programs/",
    duplicateProgram: (programUUID) =>
      `/api/admin/programs/${programUUID}/duplicate/`,
    enrollmentProcessing: () => "/api/admin/enrollment-processing/",
    cohorts: () => "/api/admin/cohorts/",
    organizations: () => "/api/admin/organizations/",
    organizationToProgram: () => "/api/admin/organization-to-program/",
    organizationToProgramAutomation: () =>
      "/api/admin/organization-to-program-automation/",
    tests: (testUUID) => "/api/admin/tests/" + (testUUID ? `${testUUID}/` : ""),
    categories: (categoryUUID) =>
      "/api/admin/categories/" + (categoryUUID ? `${categoryUUID}/` : ""),
    challenges: (challengeUUID) =>
      "/api/admin/challenges/" + (challengeUUID ? `${challengeUUID}/` : ""),
    generateProgramData: () => "/api/admin/generate-program-data/",
    studentWeeks: () => "/api/admin/student-weeks/",
    studentProfiles: (studentUUID) =>
      "/api/admin/student-profiles/" + (studentUUID ? `${studentUUID}/` : ""),
    bulkStudentImport: () => "/api/admin/student-profiles/bulk/",
    studentChats: (studentUUID) =>
      `/api/admin/student-profiles/${studentUUID}/chats/`,
    lessons: (lessonUUID) =>
      "/api/admin/lessons/" + (lessonUUID ? `${lessonUUID}/` : ""),
    batchTestQuestions: () => "/api/admin/test-questions/batch/",
    batchTestQuestionImages: () => "/api/admin/test-questions/batch-images/",
    testQuestions: (testQuestionId) =>
      "/api/admin/test-questions/" +
      (testQuestionId ? `${testQuestionId}/` : ""),
    testQuestionImages: (testQuestionImageId) =>
      "/api/admin/test-question-images/" +
      (testQuestionImageId ? `${testQuestionImageId}/` : ""),
    uploadTestQuestionPhoto: () => "/api/admin/test-questions/upload-image/",
    videos: () => "/api/admin/videos/",
    subjectTests: () => "/api/admin/subject-tests/",
    fullTests: () => "/api/admin/full-tests/",
    aggregatedAnalysis: (aggId) =>
      aggId
        ? `/api/admin/aggregated-analysis/${aggId}/`
        : "/api/admin/aggregated-analysis/",
    mockActEvents: (mockActEventId) =>
      "/api/admin/mock-act-events/" +
      (mockActEventId ? `${mockActEventId}/` : ""),
    ticketTailorQuestionMapping: (modelName) =>
      `/api/admin/ticket-tailor-mapping/${modelName}/`,
    startScores: (studentId) => `/api/admin/start-scores/${studentId}/`,
    imageUpload: (studentId) => `/api/admin/students/${studentId}/image/`,
    accountability: () => "/api/admin/accountability/",
    productivity: () => "/api/admin/productivity/",
    master: () => "/api/admin/master/",
    masterV2: () => "/api/admin/master-v2/",
    masterV2Layer: (coachingLayer) =>
      `/api/admin/master-v2/layer/${coachingLayer}/`,
    masterV2Row: (rowType, rowId) =>
      `/api/admin/master-v2/row/${rowType}/${rowId}/`,
    accountabilitySheetStudent: () => "/api/admin/accountability/student/",
    goodNews: (goodNewsType) => `/api/admin/good-news/${goodNewsType}/`,
    teams: (teamId) =>
      teamId ? `/api/admin/teams/${teamId}/` : "/api/admin/teams/",
    roles: (roleId) =>
      roleId ? `/api/admin/roles/${roleId}/` : "/api/admin/roles/",
    adminProfiles: (adminProfileId) =>
      adminProfileId
        ? `/api/admin/admin-profiles/${adminProfileId}/`
        : "/api/admin/admin-profiles/",
    teamToProgram: (teamToProgramId) =>
      teamToProgramId
        ? `/api/admin/team-to-program/${teamToProgramId}/`
        : "/api/admin/team-to-program/",
    roleToProgram: (roleToProgramId) =>
      roleToProgramId
        ? `/api/admin/role-to-program/${roleToProgramId}/`
        : "/api/admin/role-to-program/",
    userProfileToProgram: (userProfileToProgramId) =>
      userProfileToProgramId
        ? `/api/admin/user-profile-to-program/${userProfileToProgramId}/`
        : "/api/admin/user-profile-to-program/",
    buildSchedule: (date, userProfileId) =>
      date
        ? userProfileId
          ? `/api/admin/schedule/${userProfileId}/${date}/`
          : `/api/admin/schedule/${date}/`
        : `/api/admin/schedule/${dayjs().format("YYYY-MM-DD")}/`,
    scheduleStatus: (userProfileId) =>
      userProfileId
        ? `/api/admin/schedule-status/${userProfileId}/`
        : "/api/admin/schedule-status/",
    weekStats: () => "/api/admin/week-stats/",
    employeeCoverage: (employeeCoverageId) =>
      employeeCoverageId
        ? `/api/admin/employee-coverage/${employeeCoverageId}/`
        : "/api/admin/employee-coverage/",
    toDoEvents: () => "/api/admin/to-do-events/",
    toDoEventTriggers: (toDoEventTriggerId) =>
      toDoEventTriggerId
        ? `/api/admin/to-do-event-triggers/${toDoEventTriggerId}/`
        : "/api/admin/to-do-event-triggers/",
  },
  users: {
    profile: (userProfileId) =>
      "/api/users/profile/" + (userProfileId ? `${userProfileId}/` : ""),
    changeEmail: () => "/api/users/change-email/",
    password: () => `/api/users/password/`,
    emailVerificationLink: () => "/api/users/email-verification-link/",
    phoneVerificationLink: () => "/api/users/phone-number-verification-link/",
  },
  testing: {
    subjectTest: () => "/api/testing/subject-test/",
    fullTests: () => "/api/testing/full-tests/",
    fullTest: (fullTestId) => `/api/testing/full-test/${fullTestId}/`,
    skipFullTestSubject: (fullTestId, subject) =>
      `/api/testing/full-test/${fullTestId}/skip-subject/${subject}/`,
    analysis: () => "/api/testing/analysis/",
    aiGeneratedQuestions: () => "/api/testing/rated-questions/",
    questionRatings: (rowUUID) => "api/testing/question-rating/" + rowUUID,
    generateQuestion: () => "/api/testing/generate-question/",
    viewQuestion: (aiQuestionId) =>
      `/api/testing/view-ai-question/${aiQuestionId}/`,
    rateQuestion: (aiQuestionId) =>
      `/api/testing/rate-ai-question/${aiQuestionId}/`,
    timeline: (subject) => `/api/testing/timeline/${subject}/`,
    categoriesMissed: () => `/api/testing/categories-missed/`,
    scores: () => `/api/testing/scores/`,
    videoRatings: (videoId) => `/api/testing/video-ratings/${videoId}/`,
    tests: () => `/api/testing/tests/`,
    testQuestions: (testQuestionId) =>
      testQuestionId
        ? `/api/testing/questions/${testQuestionId}/`
        : `/api/testing/questions/`,
    challenges: () => `/api/testing/challenges/`,
    aggregatedAnalysis: (id) => `/api/testing/aggregated-analysis/${id}/`,
    mockACTEvent: (code) => `/api/testing/mock-act-event/${code}/`,
  },
  scheduling: {
    completedToDoItems: (userProfileId) =>
      userProfileId
        ? `/api/scheduling/to-do-items/complete/${userProfileId}/`
        : "/api/scheduling/to-do-items/complete/",
    toDoItems: (userProfileId) =>
      userProfileId
        ? `/api/scheduling/to-do-items/${userProfileId}/`
        : "/api/scheduling/to-do-items/",
    toDoItem: (toDoInstanceId) =>
      `/api/scheduling/to-do-item/${toDoInstanceId}/`,
    delegateToDoItem: (toDoInstanceId) =>
      `/api/scheduling/to-do-item/${toDoInstanceId}/delegate/`,
    scheduleItemCompletion: (userProfileId) =>
      userProfileId
        ? `/api/scheduling/schedule-item-completion/${userProfileId}/`
        : "/api/scheduling/schedule-item-completion/",
    toDoListOrdering: (userProfileId) =>
      withUserProfileId("/api/scheduling/to-do-list-ordering/", userProfileId),
    toDoFolders: (userProfileId) =>
      withUserProfileId("/api/scheduling/to-do-folders/", userProfileId),
    toDoFolder: (folderId) => `/api/scheduling/to-do-folder/${folderId}/`,
    toDoFolderOrdering: (userProfileId) =>
      withUserProfileId("/api/scheduling/to-do-folder-ordering/", userProfileId),
  },
  clock: {
    status: () => "/api/admin/clock/status/",
    in: () => "/api/admin/clock/in/",
    out: () => "/api/admin/clock/out/",
    events: (clockEventId) =>
      clockEventId
        ? `/api/admin/clock/events/${clockEventId}/`
        : "/api/admin/clock/events/",
    timeSubmission: () => "/api/admin/time-submission/",
    checkIn: () => "/api/admin/checkin/",
  },
  messaging: {
    token: () => "/api/messaging/token/",
    users: () => "/api/messaging/users/",
    channels: () => "/api/messaging/channels/",
    channelFilters: () => "/api/messaging/channels/filters/",
    channel: (channelId) => `/api/messaging/channels/${channelId}/`,
  },
};

export const wsUrls = {
  testing: {
    takeTest: (testId) => `/ws/testing/take-test/${testId}/`,
  },
  messaging: {
    inbox: () => "/ws/messaging/inbox/",
    conversation: (conversationId) =>
      `/ws/messaging/conversations/${conversationId}/`,
    notifications: () => "/ws/messaging/notify/",
  },
};

export const urls = {
  error: (errorCode) => `/error/${errorCode}`,
  sso: {
    authorization: () => `${apiOrigin}/o/authorize/`,
    token: () => `${apiOrigin}/o/token/`,
    redirect: () => `${origin}/oauth/callback/`,
  },
  users: {
    logout: () => `${apiOrigin}/users/logout/`,
    login: () => `${apiOrigin}/users/login/`,
  },
  students: {
    base: () => `${apiOrigin}/student/`,
    dashboard: () => `${apiOrigin}/student/?section=dashboard`,
    weekStats: () => `${apiOrigin}/student/?section=week-stats`,
    weekReports: () => `${apiOrigin}/student/?section=week-report`,
    preSubjectReports: () =>
      `${apiOrigin}/student/?section=pre-subject-reports`,
    goodNews: () => `${apiOrigin}/student/?section=good-news`,
    internal: {
      base: () => "/student/",
      dashboard: () => "/student/dashboard/",
      weekStats: () => "/student/week-stats/",
      weekReports: () => "/student/week-reports/",
      preSubjectReports: () => "/student/pre-subject-reports/",
      goodNews: () => "/student/good-news/",
    },
    impersonate: (studentUuid) => `/impersonate/${studentUuid}/`,
  },
  testing: {
    home: {
      base: () => "/",
      dashboard: () => "/",
      myTests: () => "/tests/",
      takeTest: () => "/take-test/",
    },
    testing: {
      base: () => "/testing/",
      subjectTest: (subjectTestUUID) =>
        `/testing/subject-test/${subjectTestUUID}/`,
      fullTest: (fullTestUUID) => `/testing/full-test/${fullTestUUID}/`,
      analysis: (subjectTestUUID) => `/testing/analysis/${subjectTestUUID}/`,
    },
    admin: {
      base: () => "/admin/",
      reports: () => "/admin/reports/",
      tests: () => "/admin/tests/",
      aggregatedAnalysis: (urlParams) => {
        if (typeof urlParams == "object") {
          return `/testing/aggregated-analysis/?${urlParams.toString()}`;
        }
        return `/testing/aggregated-analysis/${urlParams}/`;
      },
    },
  },
  admin: {
    impersonate: (profileType, profileId) =>
      `${apiOrigin}/admin/impersonate/${profileType}/${profileId}/`,
    dashboard: {
      base: () => "/",
    },
    coaching: {
      base: () => "/coaching/",
      accountability: () => "/coaching/accountability/",
      master: () => "/coaching/master/",
      masterV2: () => "/coaching/master-v2/",
      goodNews: (goodNewsType) => `/coaching/good-news/${goodNewsType}/`,
      teamManagement: () => "/coaching/team-management/",
    },
    curriculum: {
      base: () => "/curriculum/",
      curriculumBuilder: () => "/curriculum/build/",
      tests: () => "/curriculum/tests/",
      challenges: () => "/curriculum/challenges/",
      lessons: () => "/curriculum/lessons/",
      categories: () => "/curriculum/categories/",
      aiPractice: () => "/curriculum/ai-practice/",
      aiAdmin: () => "/curriculum/ai-admin/",
      aiTestAnalysis: () => "/curriculum/ai-test-analysis/",
    },
    programManagement: {
      base: () => "/program/",
      orders: () => "/program/orders/",
      enrollmentProcessing: () => "/program/enrollment-processing/",
      organizations: () => "/program/organizations/",
      cohorts: () => "/program/cohorts/",
      automations: () => "/program/automations/",
    },
    employeeManagement: {
      base: () => "/employees/",
      companyTree: () => "/employees/tree/",
    },
    scheduling: {
      base: () => "/scheduling/",
      toDoItems: () => "/scheduling/to-do-items/",
      mockActs: () => "/scheduling/mock-acts/",
    },
  },
};
